import React, { useEffect, useState } from "react";
import Modal from "components/shared/Modal";
import { useForm, useWatch } from "react-hook-form";
import FileUploadField from "components/shared/form/fields/FileUploadField";
import ModalActions from "components/shared/modal/ModalActions";
import { useDispatch } from "react-redux";
import { every, filter, isEmpty, some } from "lodash";

type FileUploadModalArgs = {
  chatId: string;
  close: () => void;
  selectedFileAttachmentsCount: number;
  maxFiles?: number;
  isImageUploader?: boolean;
};
export default function FileUploadModal({
  chatId,
  close,
  selectedFileAttachmentsCount,
  maxFiles = 10,
  isImageUploader = false,
}: FileUploadModalArgs) {
  const dispatch = useDispatch();
  const [maxFilesReached, setMaxFilesReached] = useState(false);

  const { handleSubmit, control, setValue } = useForm<{
    fileAttachments: File[] | null;
  }>();

  const selectedFileAttachments = useWatch({
    control,
    name: "fileAttachments",
  });

  useEffect(() => {
    if (selectedFileAttachments) {
      setMaxFilesReached(
        filter(selectedFileAttachments, { state: "volatile" }).length +
          selectedFileAttachmentsCount >
          maxFiles,
      );
    }
  }, [selectedFileAttachments]);

  function submit({ fileAttachments }) {
    dispatch({
      type: isImageUploader
        ? "chat/COMPOSER/ADD_IMAGE_ATTACHMENTS"
        : "chat/COMPOSER/ADD_FILE_ATTACHMENTS",
      chatId,
      fileAttachments: filter(fileAttachments, { state: "volatile" }),
    });
    close();
  }

  return (
    <Modal
      title={
        isImageUploader
          ? I18n.t("js.chat.composer.image_upload_modal.title")
          : I18n.t("js.chat.composer.file_upload_modal.title")
      }
      onClose={close}
    >
      <form
        onSubmit={handleSubmit(submit)}
        className="flex flex-col h-full justify-between min-h-[400px]"
      >
        <div className="mb-4">
          <div className="alert alert-info">
            {selectedFileAttachmentsCount === 0
              ? I18n.t(
                  isImageUploader
                    ? "js.chat.composer.files_upload_modal.without_selected.max_images_error"
                    : "js.chat.composer.file_upload_modal.without_selected.max_files_error",
                  { max_files: maxFiles },
                )
              : I18n.t(
                  isImageUploader
                    ? "js.chat.composer.file_upload_modal.max_images_error"
                    : "js.chat.composer.file_upload_modal.max_files_error",
                  {
                    count: maxFiles - selectedFileAttachmentsCount,
                    max_files: maxFiles,
                  },
                )}
          </div>
          <FileUploadField
            name="fileAttachments"
            control={control}
            options={{
              multiple: true,
              maxFiles: maxFiles - selectedFileAttachmentsCount,
              isImage: isImageUploader,
              storageDirectory: isImageUploader ? "images" : "files",
            }}
          />
        </div>
        <ModalActions
          submitText={
            isImageUploader
              ? "js.chat.composer.image_upload_modal.submit_text"
              : "js.chat.composer.file_upload_modal.submit_text"
          }
          closeModal={() => setValue("fileAttachments", null)}
          submitDisabled={
            isEmpty(selectedFileAttachments) ||
            maxFilesReached ||
            some(selectedFileAttachments, { state: "uploading" }) ||
            every(selectedFileAttachments, { state: "removed" })
          }
        />
      </form>
    </Modal>
  );
}
